'use client';

import { useEffect, useMemo, useState } from 'react';
import { computeSleep } from '@/lib/browser-healthfin-tools';

type Direction = 'bedtime' | 'wake';

export default function BrowserSleepCalculator() {
  const [direction, setDirection] = useState<Direction>('bedtime');
  const [time, setTime] = useState('07:00');

  // Default the input to the current time on the client only (SSR-safe).
  useEffect(() => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTime(`${hh}:${mm}`);
  }, []);

  const result = useMemo(() => computeSleep(time, direction), [time, direction]);

  const prompt =
    direction === 'bedtime'
      ? 'If you want to wake up at this time, go to bed at one of these times:'
      : 'If you go to bed at this time, aim to wake up at one of these times:';

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Sleep</span>
        <h2 className="tool-live-title">Sleep Cycle Calculator</h2>
        <p className="tool-live-subtitle">
          Plan your night around 90-minute sleep cycles. Pick a wake-up time to see when to go to
          bed, or a bedtime to see when to wake. Everything runs in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>I want to calculate</span>
              <select
                onChange={(event) => setDirection(event.target.value as Direction)}
                value={direction}
              >
                <option value="bedtime">Bedtimes from a wake-up time</option>
                <option value="wake">Wake times from a bedtime</option>
              </select>
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>{direction === 'bedtime' ? 'Wake-up time' : 'Bedtime'}</span>
              <input onChange={(event) => setTime(event.target.value)} type="time" value={time} />
            </label>
          </div>
          <p className="tool-live-panel-note">
            Each cycle is about 90 minutes, and we add roughly 15 minutes to fall asleep. Waking at
            the end of a cycle helps you feel more refreshed.
          </p>
        </section>

        <section className="tool-live-preview">
          {!result.ok ? (
            <p className="status-message" style={{ color: '#b91c1c' }}>
              {result.message}
            </p>
          ) : (
            <>
              <p style={{ marginBottom: '0.75rem', fontSize: '0.9rem', opacity: 0.85 }}>{prompt}</p>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {(result.suggestions ?? []).map((suggestion) => (
                  <div
                    key={suggestion.cycles}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: '1rem',
                      borderBottom: '1px solid rgba(148, 163, 184, 0.25)',
                      paddingBottom: '0.4rem',
                    }}
                  >
                    <strong style={{ fontSize: '1.05rem' }}>{suggestion.time}</strong>
                    <span style={{ fontSize: '0.85rem', opacity: 0.75 }}>
                      {suggestion.cycles} cycles · {suggestion.hours}
                      {suggestion.recommended ? ' · recommended' : ''}
                    </span>
                  </div>
                ))}
              </div>
              <p className="tool-live-panel-note">
                Most adults do best with 5 to 6 complete cycles (about 7.5 to 9 hours of sleep).
              </p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
